import credits from '../../media/credits.json'
import { srcFor, srcSetFor, dimensionsFor } from './srcset'
import type { OwnCredit } from '../../jsonld'

/**
 * One manifest photo as AVIF → WebP → JPEG `<picture>`.
 *
 * The box is sized before the file arrives (`aspect-ratio` + `width`/`height`
 * from credits.json) and painted with a surface→forest gradient, so a slow or
 * missing image never reshapes the layout. `priority` is for the one
 * above-the-fold image of a page (it is also preloaded from head.ts).
 * `fill` drops the aspect ratio: the box then takes its size from the
 * parent, through `className` (e.g. `absolute inset-0`).
 */
export function Picture({ role, alt, sizes, priority = false, className, imgClassName, aspect, fill = false }: { role: string; alt: string; sizes: string; priority?: boolean; className?: string; imgClassName?: string; aspect?: 'photo' | 'wide' | 'square'; fill?: boolean }) {
  const { width, height } = dimensionsFor(role, credits as OwnCredit[])
  const ratio = aspect === 'wide' ? '16 / 9' : aspect === 'square' ? '1 / 1' : `${width} / ${height}`
  return (
    <picture className={className} style={{ display: 'block', aspectRatio: fill ? undefined : ratio, background:'linear-gradient(160deg, rgb(var(--vc2-surface)), rgb(var(--vc2-forest) / .55))' }}>
      <source type="image/avif" srcSet={srcSetFor(role, 'avif')} sizes={sizes} />
      <source type="image/webp" srcSet={srcSetFor(role, 'webp')} sizes={sizes} />
      <img src={srcFor(role, 1600, 'jpg')} srcSet={srcSetFor(role, 'jpg')} sizes={sizes} alt={alt} width={width} height={height}
           loading={priority ? 'eager' : 'lazy'} decoding={priority ? 'sync' : 'async'} fetchPriority={priority ? 'high' : 'auto'}
           className={imgClassName ?? 'h-full w-full object-cover'} />
    </picture>
  )
}
